'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import StepWelcome from './StepWelcome';
import StepGoals from './StepGoals';
import StepSupportStyle from './StepSupportStyle';
import StepAuth from './StepAuth';

export type OnboardingData = {
  goals: string[];
  supportStyle: string[];
  name: string;
  email: string;
};

export default function OnboardingFlow() {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    goals: [],
    supportStyle: [],
    name: '',
    email: '',
  });

  // If already authenticated, redirect to home
  React.useEffect(() => {
    if (!loading && user) {
      router.replace('/home-screen');
    }
  }, [user, loading, router]);

  const totalSteps = 4;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const handleComplete = () => {
    router.push('/home-screen');
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg flex flex-col">
      {/* Progress dots */}
      {step > 0 && (
        <div className="flex items-center justify-center gap-2 pt-12 pb-4 fade-in">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={`dot-${i}`}
              className={`rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-6 h-2 bg-primary'
                  : i < step
                  ? 'w-2 h-2 bg-primary/40' :'w-2 h-2 bg-border'
              }`}
            />
          ))}
        </div>
      )}

      {/* Step content */}
      <div className="flex-1 flex flex-col">
        {step === 0 && <StepWelcome onNext={handleNext} />}
        {step === 1 && (
          <StepGoals
            selected={data.goals}
            onChange={(goals) => setData((d) => ({ ...d, goals }))}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 2 && (
          <StepSupportStyle
            selected={data.supportStyle}
            onChange={(supportStyle) => setData((d) => ({ ...d, supportStyle }))}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 3 && (
          <StepAuth
            data={data}
            onChange={(partial) => setData((d) => ({ ...d, ...partial }))}
            onComplete={handleComplete}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
}